import React, { useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, EyeOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { documentService, type Document } from '@/services/document.service';
import {
  UAE_DOC_ALLOWED_EXTENSIONS,
  UAE_DOC_MAX_BYTES,
  employeeDocumentCategoryForPack,
  employeeDocumentTypesForPack,
} from '@/pages/hr/shared/uaeEmployeeDocuments';
import { useHrmsPolicy } from '@/hooks/useHrmsPolicy';

const { Text } = Typography;
const ENTITY_TYPE = 'Employee';

type Props = {
  employeeId: string;
  readOnly?: boolean;
};

function extOk(name: string) {
  const lower = name.toLowerCase();
  return UAE_DOC_ALLOWED_EXTENSIONS.some((e) => lower.endsWith(e));
}

/** Pack-aware employee statutory / KYC attachments (India or UAE). */
const EmployeeUaeDocumentsCard: React.FC<Props> = ({ employeeId, readOnly }) => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { indiaPack, pack } = useHrmsPolicy();
  const docTypes = employeeDocumentTypesForPack(pack);
  const docCategory = employeeDocumentCategoryForPack(pack);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();
  const [file, setFile] = useState<File | null>(null);

  const queryKey = useMemo(
    () => ['documents', ENTITY_TYPE, employeeId, indiaPack ? 'india' : 'uae'],
    [employeeId, indiaPack]
  );

  const { data: docs = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => documentService.getDocuments({ entity_type: ENTITY_TYPE, entity_id: employeeId }),
  });

  const byType = useMemo(() => {
    const map = new Map<string, Document[]>();
    for (const d of docs) {
      const t = d.document_type || 'Other';
      if (!map.has(t)) map.set(t, []);
      map.get(t)!.push(d);
    }
    return map;
  }, [docs]);

  const coverage = docTypes.map((t) => ({
    ...t,
    count: byType.get(t.value)?.length || 0,
    latest: byType.get(t.value)?.[0],
  }));

  const defaultType = indiaPack ? 'PAN_Card' : 'Passport';
  const titleNoun = indiaPack ? 'India statutory' : 'UAE compliance';

  const upload = async () => {
    try {
      const v = await form.validateFields();
      if (!file) {
        message.error('Choose a file');
        return;
      }
      if (!extOk(file.name)) {
        message.error(`Allowed: ${UAE_DOC_ALLOWED_EXTENSIONS.join(', ')}`);
        return;
      }
      if (file.size > UAE_DOC_MAX_BYTES) {
        message.error('File exceeds 25MB');
        return;
      }
      setUploading(true);
      await documentService.uploadDocument(file, {
        entity_type: ENTITY_TYPE,
        entity_id: employeeId,
        document_type: v.document_type,
        document_category: docCategory,
        document_name: v.document_name || file.name,
        expiry_date: v.expiry_date ? v.expiry_date.format('YYYY-MM-DD') : undefined,
        description: v.description,
      });
      message.success('Document uploaded');
      setOpen(false);
      setFile(null);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey });
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const columns: ColumnsType<Document> = [
    {
      title: 'Type',
      dataIndex: 'document_type',
      width: 140,
      render: (t: string) => docTypes.find((x) => x.value === t)?.label || t || '—',
    },
    {
      title: 'Name',
      dataIndex: 'document_name',
      ellipsis: true,
      render: (_: unknown, r) => r.document_name || r.file_name,
    },
    {
      title: 'Expiry',
      dataIndex: 'expiry_date',
      width: 120,
      render: (d: string) => {
        if (!d) return '—';
        const days = dayjs(d).diff(dayjs(), 'day');
        return (
          <Space>
            <span>{dayjs(d).format('DD-MMM-YYYY')}</span>
            {days <= 90 && <Tag color={days < 0 ? 'red' : 'orange'}>{days < 0 ? 'Expired' : `${days}d`}</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Uploaded',
      dataIndex: 'created_at',
      width: 120,
      render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    {
      title: '',
      key: 'a',
      width: 110,
      render: (_, r) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => void documentService.openDocumentInNewTab(r.id)}
          />
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => void documentService.downloadDocument(r.id)}
          />
          {!readOnly && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={async () => {
                await documentService.deleteDocument(r.id);
                message.success('Removed');
                queryClient.invalidateQueries({ queryKey });
              }}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={`${titleNoun} attachments`}
        description={
          indiaPack
            ? 'Upload PAN, Aadhaar, UAN/PF proof, ESI card, bank proof, appointment letter, and related files. Stored on the Document module (entity Employee) and used by Document expiry.'
            : 'Upload Passport, Emirates ID, Visa, Labour card, Work permit, Insurance, and related files. Stored on the Document module (entity Employee) and used by Document expiry.'
        }
      />

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {coverage.map((c) => (
          <Col xs={12} sm={8} md={6} lg={4} key={c.value}>
            <Card size="small" style={{ height: '100%' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {c.label}
              </Text>
              <div>
                {c.count > 0 ? (
                  <Tag color="green">{c.count} file(s)</Tag>
                ) : (
                  <Tag>Missing</Tag>
                )}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        title="Attached files"
        extra={
          !readOnly && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                form.setFieldsValue({ document_type: defaultType });
                setOpen(true);
              }}
            >
              Upload document
            </Button>
          )
        }
      >
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={docs}
          pagination={{ pageSize: 10 }}
          locale={{
            emptyText: indiaPack
              ? 'No documents yet — upload PAN, Aadhaar, UAN/PF, ESI…'
              : 'No documents yet — upload Passport, Emirates ID, Visa, Labour card…',
          }}
        />
      </Card>

      <Modal
        title={`Upload ${indiaPack ? 'India' : 'UAE'} employee document`}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={upload}
        confirmLoading={uploading}
        okText="Upload"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="document_type" label="Document type" rules={[{ required: true }]}>
            <Select
              options={docTypes.map((t) => ({
                value: t.value,
                label: t.label,
              }))}
            />
          </Form.Item>
          <Form.Item name="document_name" label="Display name" help="Defaults to file name if blank">
            <Input placeholder={indiaPack ? 'e.g. PAN card scan' : 'e.g. Passport bio page'} />
          </Form.Item>
          <Form.Item name="expiry_date" label="Expiry date" help="Used by Document expiry monitoring">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label="File" required>
            <Upload
              maxCount={1}
              beforeUpload={(f) => {
                setFile(f);
                return false;
              }}
              onRemove={() => setFile(null)}
              accept={UAE_DOC_ALLOWED_EXTENSIONS.join(',')}
            >
              <Button>Select file</Button>
            </Upload>
            {file && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                {file.name}
              </Text>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default EmployeeUaeDocumentsCard;
